// Copyright (c) StrangeBee 2024

import PreviewButton from '@Atoms/Buttons/PreviewButton/PreviewButton';
import { IconLabel } from '@Atoms/Info';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useDrawer } from '@Hooks/useDrawer';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { AlertDrawer } from '@Organisms/Alert/AlertDrawer/AlertDrawer';
import { computeEventList } from '@Organisms/Alert/eventList';
import { DisplayEventProps } from '@Organisms/Timeline/DisplayEvent';
import { EventCard } from '@Organisms/Timeline/EventCard';
import { useCurrentUser, useListAllUsers } from '@Queries/userQueries';
import React, { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { AlertOutput, PermissionsEnum } from 'thehive-sdk';

import './AlertContent.scss';

export const AlertContent = (props: DisplayEventProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();
    const { data: userList = [] } = useListAllUsers();
    const { visible, setVisible } = useDrawer<AlertOutput>({});
    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_ALERT_UPDATE]);

    const data = props.currentEvent.details?.alert;

    const { _createdBy, title } = data || {};
    const { currentEvent, refetch } = props;

    return (
        <EventCard {...props}>
            <div className="alert-content card__content">
                <div className="alert-content__header card__header">
                    <div className="alert-content__title">{title}</div>
                </div>

                {_createdBy && (
                    <THEditable
                        name="_createdBy"
                        className="card-info__user"
                        initialValues={{ _createdBy }}
                        label={i('common.created-by')}
                    >
                        <THSelect disabled borderless>
                            {userList?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    </THEditable>
                )}

                {currentEvent.date && (
                    <THEditable
                        name="date"
                        className="card-info__date"
                        initialValues={{ date: currentEvent.date }}
                        label={i('common.start-date')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}
                {data && (
                    <div className="card__preview">
                        <PreviewButton onClick={() => setVisible(true)} />
                    </div>
                )}

                {visible && (
                    <AlertDrawer
                        data={data}
                        onVisibleChange={setVisible}
                        visible={visible}
                        eventList={computeEventList({
                            controller: client.AlertController,
                            locale: i,
                            onSuccess: (_, message) => {
                                message && openMessage('success', message);
                                refetch?.();
                            },
                            history,
                            onError: handleNotificationError,
                        })}
                        hasPermissions={hasPermissions}
                    />
                )}
            </div>
        </EventCard>
    );
};
